import { auth } from '@/state/auth'
import axios from 'axios'
import type { Post } from 'functions/_db/schema'
import type { PostPatchParams } from 'functions/api/posts/[id]'
import type { GenerateParams, GenerateResult } from 'functions/api/posts/generate'
import type { PostsResponse } from 'functions/api/posts/index'
import { produce } from 'immer'
import { create } from 'zustand'

type PostsState = {
  empty: boolean
  list: Post[]
  loading: boolean
  generating: boolean
  hasMore: boolean
  cursor: number | null
  fetch: () => Promise<void>
  fetchMore: () => Promise<void>
  generate: (params: GenerateParams) => Promise<GenerateResult>
  patch: (id: number, data: PostPatchParams, silent?: boolean) => Promise<void>
  setContent: (id: number, content: string) => void
  setProject: (id: number, projectId: number) => void
  remove: (id: number) => Promise<void>
  reset: () => void
}

export const usePostsState = create<PostsState>((set, get) => ({
  list: [],
  empty: false,
  loading: false,
  generating: false,
  hasMore: false,
  cursor: null,

  fetch: async () => {
    set({ loading: true })
    try {
      const { data } = await auth(token => (
        axios.get<PostsResponse>('/api/posts', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      ))

      set({
        list: data.posts,
        empty: data.posts.length === 0,
        hasMore: data.hasMore,
        cursor: data.nextCursor,
      })
    }
    catch (e) {
      console.error(e)
      set({ list: [] })
    }
    finally {
      set({ loading: false })
    }
  },

  fetchMore: async () => {
    const { cursor, hasMore, loading } = get()
    if (!hasMore || loading) return

    set({ loading: true })
    try {
      const { data } = await auth(token => (
        axios.get<PostsResponse>(`/api/posts?cursor=${cursor}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ))
      set(state => ({
        list: [...state.list, ...data.posts],
        hasMore: data.hasMore,
        cursor: data.nextCursor,
      }))
    }
    catch (e) {
      console.error(e)
    }
    finally {
      set({ loading: false })
    }
  },

  generate: async params => {
    set({ generating: true })
    try {
      const { data } = await auth(token => (
        axios.post<GenerateResult>('/api/posts/generate', params, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      ))

      set(produce((s: PostsState) => {
        s.list.unshift(data.post)
      }))

      return data
    }
    catch (e) {
      console.error(e)
      throw e
    }
    finally {
      set({ generating: false })
    }
  },

  setContent: (id, content) => {
    set(produce((s: PostsState) => {
      const index = s.list.findIndex(p => p.id === id)
      if (index !== -1) {
        s.list[index].content = content
      }
    }))
  },

  setProject: (id, projectId) => {
    set(produce((s: PostsState) => {
      const index = s.list.findIndex(p => p.id === id)
      if (index !== -1) {
        s.list[index].projectId = projectId
      }
    }))
  },

  patch: async (id, data) => {
    try {
      await auth(token => (
        axios.patch<Post>(`/api/posts/${id}`, data, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      ))
    }
    catch (e) {
      console.error(e)
    }
  },

  remove: async id => {
    try {
      await auth(token => (
        axios.delete(`/api/posts/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      ))
      set(state => {
        const list = state.list.filter(p => p.id !== id)
        return ({
          list,
          empty: list.length === 0,
        })
      })
    }
    catch (e) {
      console.error(e)
    }
  },

  reset: () => {
    set({
      list: [],
      loading: false,
      generating: false,
      empty: false,
      hasMore: false,
      cursor: null,
    })
  },
}))
